import React from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Typography,
  Paper,
  Tooltip,
  Checkbox,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  Clear as ClearIcon,
  Check as CheckIcon,
  Save as SaveIcon,
  RequestQuote as RequestQuoteIcon
} from '@mui/icons-material';
import { formatCurrency } from '../../utils/calculations';
import { TRANSACTION_TYPES, CREDIT_TRANSACTION_TYPES } from '../../constants';
import CustomerAutocomplete from './CustomerAutocomplete';

const TransactionHeader = ({
  type,
  customerName,
  onCustomerNameChange,
  account,
  accounts = [],
  onAccountChange,
  orderNumber,
  onOrderNumberChange,
  vatExempt = false,
  onVatExemptChange,
  quoteMode = 'cash',
  onQuoteModeChange,
  totals = { subtotal: 0, vat: 0, total: 0 },
  onAddRow,
  onClear,
  onSubmit,
  showSaveAsQuote = false,
  onSaveAsQuote,
  saveAsQuoteEnabled = false,
  canAddRow = true,
  canSubmit = false,
  isSubmitting = false,
}) => {
  const isQuote = type === TRANSACTION_TYPES.QUOTE;
  const isCashOrRefund = type === TRANSACTION_TYPES.CASH_SALE || type === TRANSACTION_TYPES.REFUND;
  const isInvoiceOrCredit = type === TRANSACTION_TYPES.INVOICE || type === TRANSACTION_TYPES.CREDIT_NOTE;
  const isCreditType = CREDIT_TRANSACTION_TYPES.includes(type);
  // A Quote isn't inherently a cash-customer or account transaction the way
  // the other 4 types are - which field it shows depends on the toggle below.
  const showCustomerField = isCashOrRefund || (isQuote && quoteMode === 'cash');
  const showAccountField = isInvoiceOrCredit || (isQuote && quoteMode === 'account');

  // Saving a Cash Sale as a quote needs a customer name even though the real
  // Cash Sale itself doesn't - call that out specifically rather than just
  // leaving the button silently disabled.
  const saveAsQuoteNeedsCustomerName = type === TRANSACTION_TYPES.CASH_SALE && !customerName?.trim();
  const saveAsQuoteTooltip = saveAsQuoteNeedsCustomerName
    ? 'Save as Quote - Customer Name Required'
    : 'Save as Quote';

  // Same call-out on the Quote form's own Submit button, when it's in cash
  // mode (an account-mode Quote's missing-account case isn't as easily
  // misread, since "Choose Account" is a visibly empty required field).
  const submitNeedsCustomerName = isQuote && quoteMode === 'cash' && !customerName?.trim();
  const submitTooltip = isSubmitting
    ? 'Submitting...'
    : submitNeedsCustomerName
      ? 'Submit - Customer Name Required'
      : 'Submit';

  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        mb: 2,
        // Stays visible for as long as this form is on screen (unlike the
        // page-top Alert, which scrolls away) - a persistent reminder that
        // this is a Refund/Credit Note, not a Cash Sale/Invoice.
        ...(isCreditType && {
          borderLeft: '6px solid',
          borderColor: 'warning.main',
          bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08),
        }),
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 3 }}>
        {/* Left Side - Customer/Account and Order Number */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Cash Customer / Account toggle (Quote only) - a Quote can be
              made out to either, unlike the other 4 transaction types where
              this is fixed by the form itself. */}
          {isQuote && (
            <ToggleButtonGroup
              value={quoteMode}
              exclusive
              size="small"
              onChange={(e, newMode) => {
                if (newMode) onQuoteModeChange(newMode);
              }}
              sx={{ height: 40 }}
            >
              <ToggleButton value="cash">Cash Customer</ToggleButton>
              <ToggleButton value="account">Account</ToggleButton>
            </ToggleButtonGroup>
          )}

          {/* Customer Name (Cash Sale / Refund / Quote in cash mode) */}
          {showCustomerField && (
            <CustomerAutocomplete
              label="Customer Name"
              placeholder="Enter customer name (optional)"
              value={customerName || ''}
              onChange={onCustomerNameChange}
              sx={{ minWidth: 250 }}
            />
          )}

          {/* Account Selector (Invoice / Credit Note / Quote in account mode) */}
          {showAccountField && (
            <FormControl size="small" sx={{ minWidth: 250 }}>
              <InputLabel>Choose Account</InputLabel>
              <Select
                value={account?.id || ''}
                onChange={(e) => {
                  const selectedAccount = accounts.find(acc => acc.id === e.target.value);
                  onAccountChange(selectedAccount);
                }}
                label="Choose Account"
              >
                <MenuItem value="">
                  <em>Choose Account</em>
                </MenuItem>
                {accounts.map((acc) => (
                  <MenuItem key={acc.id} value={acc.id}>
                    {acc.name} ({acc.sage})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Order Number */}
          <TextField
            label="Order No."
            placeholder="Optional"
            value={orderNumber || ''}
            onChange={(e) => onOrderNumberChange(e.target.value)}
            size="small"
            sx={{ minWidth: 150 }}
          />

          {/* VAT Exempt - sets the whole transaction's VAT to 0% */}
          <FormControlLabel
            control={
              <Checkbox
                checked={vatExempt}
                onChange={(e) => onVatExemptChange(e.target.checked)}
                size="small"
              />
            }
            label="VAT Exempt"
            sx={{ whiteSpace: 'nowrap' }}
          />
        </Box>

        {/* Right Side - Totals and Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Totals Display - single compact row instead of 3 stacked
              label/value lines, which was the biggest driver of this card's
              height (each row needed its own line, plus box padding). */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 2,
              px: 2,
              py: 1,
              bgcolor: 'grey.50',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Subtotal <Box component="span" sx={{ fontWeight: 500, color: 'text.primary' }}>{formatCurrency(totals.subtotal)}</Box>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              VAT <Box component="span" sx={{ fontWeight: 500, color: 'text.primary' }}>{formatCurrency(totals.vat)}</Box>
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              Total {formatCurrency(totals.total)}
            </Typography>
          </Box>

          {/* Action Buttons - a row, not a stacked column, so this can't
              become the tallest thing in the header (3 stacked buttons were
              ~150px tall vs the ~40px the input fields actually need). */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* Add Row Button */}
            <Tooltip title="Add Row">
              <span>
                <IconButton
                  size="small"
                  onClick={onAddRow}
                  disabled={!canAddRow}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' },
                    '&.Mui-disabled': { bgcolor: 'grey.300', color: 'grey.500' },
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            {/* Clear Form Button */}
            <Tooltip title="Clear Form">
              <IconButton
                size="small"
                onClick={onClear}
                sx={{
                  bgcolor: 'grey.400',
                  color: 'white',
                  '&:hover': { bgcolor: 'grey.600' },
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Save as Quote Button (Cash Sale / Account Invoice only) - its
                own enablement, not canSubmit: a cash-style quote requires a
                customer name (so there's something to find it by later),
                even from Cash Sale where the real sale itself can stay
                anonymous. */}
            {showSaveAsQuote && (
              <Tooltip title={saveAsQuoteTooltip}>
                <span>
                  <IconButton
                    size="small"
                    onClick={onSaveAsQuote}
                    disabled={!saveAsQuoteEnabled || isSubmitting}
                    sx={{
                      bgcolor: 'secondary.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'secondary.dark' },
                      '&.Mui-disabled': { bgcolor: 'grey.300', color: 'grey.500' },
                    }}
                  >
                    <RequestQuoteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {/* Submit Button */}
            <Tooltip title={submitTooltip}>
              <span>
                <IconButton
                  size="small"
                  onClick={onSubmit}
                  disabled={!canSubmit || isSubmitting}
                  sx={{
                    bgcolor: 'success.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'success.dark' },
                    '&.Mui-disabled': { bgcolor: 'grey.300', color: 'grey.500' },
                  }}
                >
                  {isSubmitting ? <SaveIcon fontSize="small" /> : <CheckIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default TransactionHeader;
